import { compareDeliveryRefs } from '../../logistics/index.js';
import { type Delivery, type DeliveryBoardStatus } from '../interfaces/ILogistics.js';
import { fold } from '../utils/text.js';
import { zonedDay } from './format.js';

const DAY_MS = 24 * 60 * 60 * 1000;
export const DELIVERED_WINDOW_DAYS = 14;

/** Fixed ids of the seeded defaults, keyed to i18n so a Japanese site sees
 *  発注/進行中/納品済 until someone renames them. `ordered` lists TWO accepted
 *  stored names: 'Ordering' is the current seed, 'Ordered' is the legacy seed
 *  name a site got before the 2026-08 rename. Both still count as "unrenamed"
 *  so a pre-rename site keeps translating instead of freezing on the old
 *  English string the moment this list moved on without it. */
const DEFAULT_NAMES: Record<string, string[]> = {
  ordered: ['Ordering', 'Ordered'],
  processing: ['Processing'],
  delivered: ['Delivered'],
};

function dayDiff(later: string, earlier: string): number {
  return Math.round((Date.parse(later) - Date.parse(earlier)) / DAY_MS);
}

/** Overdue means one thing — the day came and went and the material is not
 *  here. The arrival FACT is the gate: an in-flight delivery (`deliveredAt`
 *  null) past its due date is late; an arrived one cannot be, whatever its
 *  column. It used to be gated on a set of "open" Genba workflow states, but
 *  that vocabulary is gone — the dates carry the whole story now. */
export function isOverdue(delivery: Delivery, now: Date, tz: string): boolean {
  return isOverdueOn(delivery, zonedDay(now.toISOString(), tz));
}

/** `isOverdue` against an already-resolved site day, so a sort resolves "today"
 *  once instead of once per card. */
export function isOverdueOn(delivery: Delivery, today: string): boolean {
  if (!delivery.dueDate || delivery.deliveredAt != null) return false;
  return dayDiff(today, delivery.dueDate) > 0;
}

/**
 * An arrival's verdict against its agreed date — on time, N days early, N days
 * overdue — or null with no date to measure against. Both are `YYYY-MM-DD`
 * (the arrival already resolved to the site's day), so the gap is an exact day
 * count. One source for the card's Arrived row and the drawer's Delivered
 * field, so the two can never disagree.
 */
export function arrivalVerdict(
  dueDate: string | null,
  arrivedDay: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): { tone: 'onTime' | 'early' | 'late'; text: string } | null {
  if (!dueDate) return null;
  const gap = dayDiff(arrivedDay, dueDate);
  if (gap === 0) return { tone: 'onTime', text: t('logistics.pipeline.arrival.onTime') };
  return gap < 0
    ? { tone: 'early', text: t('logistics.pipeline.arrival.early', { n: -gap }) }
    : { tone: 'late', text: t('logistics.pipeline.arrival.overdue', { n: gap }) };
}

export function overdueDays(delivery: Delivery, now: Date, tz: string): number {
  if (!delivery.dueDate || delivery.deliveredAt != null) return 0;
  return Math.max(0, dayDiff(zonedDay(now.toISOString(), tz), delivery.dueDate));
}

/** Where a card with no known column starts: the first NON-system column,
 *  matching the backend's resolve — a system column (Delivered) dragged to the
 *  front must not swallow every never-moved order as an arrival. A set with no
 *  other column falls back to its first. Undefined only with no columns. */
export function entryStatusId(statuses: DeliveryBoardStatus[]): string | undefined {
  return (statuses.find((status) => !status.system) ?? statuses[0])?.id;
}

/** Whether ANY line of the order is synced — enough to lock its status: the
 *  server refuses to move a partly synced arrival out of Delivered, so the
 *  field must not offer it. (`isSynced` on the order means EVERY line.) */
export function hasSyncedLine(order: Pick<Delivery, 'lines'>): boolean {
  return order.lines.some((line) => line.isSynced);
}

/** The one status-assignment rule: a delivery sits in its own status's column,
 *  or — the set no longer contains its id — in the entry column, so no card
 *  ever disappears. Undefined only when there are no columns at all. */
function assignedStatusId(delivery: Delivery, known: Set<string>, first: string | undefined): string | undefined {
  return known.has(delivery.status) ? delivery.status : first;
}

/** Every status gets a bucket (empty columns render); ids the set no longer
 *  contains land in the entry column so no card ever disappears. */
export function groupDeliveries(
  deliveries: Delivery[],
  statuses: DeliveryBoardStatus[],
): Map<string, Delivery[]> {
  const grouped = new Map<string, Delivery[]>(statuses.map((status) => [status.id, []]));
  const first = entryStatusId(statuses);
  const known = new Set(grouped.keys());
  for (const delivery of deliveries) {
    const id = assignedStatusId(delivery, known, first);
    if (id !== undefined) grouped.get(id)!.push(delivery);
  }
  return grouped;
}

/** How many cards each status holds — the same assignment rule as
 *  `groupDeliveries`, counted in one pass without building the buckets. */
export function countByStatus(
  deliveries: Delivery[],
  statuses: DeliveryBoardStatus[],
): Map<string, number> {
  const counts = new Map<string, number>(statuses.map((status) => [status.id, 0]));
  const first = entryStatusId(statuses);
  const known = new Set(counts.keys());
  for (const delivery of deliveries) {
    const id = assignedStatusId(delivery, known, first);
    if (id !== undefined) counts.set(id, counts.get(id)! + 1);
  }
  return counts;
}

/**
 * A card as a column renders it: the delivery on its face, the material it is
 * a stack OF, and the orders folded beneath it.
 *
 * One shape for both kinds of column, because Delivered's fold is per MATERIAL
 * and a truck carrying three of them is the face of three cards — a plain
 * `Delivery[]` could no longer say which line each card shows.
 */
export interface IColumnCard {
  /** The folded material name of a per-material stack (`IMaterialStack`).
   *  Undefined on a per-delivery card, which is about the whole truck. */
  key: string | undefined;
  face: Delivery;
  /** Older DELIVERED orders of the same material, newest first. */
  history: Delivery[];
}

/** A per-material stack (`stackDelivered`): always names its material. */
export interface IMaterialStack extends IColumnCard {
  key: string;
}

/** Every card a column renders: one per delivery, nothing folded. */
export function columnCards(cards: Delivery[]): IColumnCard[] {
  return cards.map((face) => ({ key: undefined, face, history: [] }));
}

/** Critical first, then overdue (most overdue on top), then due date ascending,
 *  undated last. */
export function sortColumnCards(cards: IColumnCard[], now: Date, tz: string): IColumnCard[] {
  // Decorate-sort-undecorate: the overdue verdict is resolved once per card,
  // not twice per comparison.
  const today = zonedDay(now.toISOString(), tz);
  const decorated = cards.map((card) => ({ card, late: isOverdueOn(card.face, today) }));
  decorated.sort(({ card: { face: first }, late: firstLate }, { card: { face: second }, late: secondLate }) => {
    if (first.isCritical !== second.isCritical) return first.isCritical ? -1 : 1;
    if (firstLate !== secondLate) return firstLate ? -1 : 1;
    if (!first.dueDate && !second.dueDate) return compareDeliveryRefs(first.ref, second.ref);
    if (!first.dueDate) return 1;
    if (!second.dueDate) return -1;
    return first.dueDate.localeCompare(second.dueDate) || compareDeliveryRefs(first.ref, second.ref);
  });
  return decorated.map(({ card }) => card);
}

/** When a delivery landed, for newest-first ordering — an undated arrival
 *  counts as newest (it just landed). */
function arrivedAt(delivery: Delivery): number {
  return delivery.deliveredAt == null ? Number.POSITIVE_INFINITY : Date.parse(delivery.deliveredAt);
}

/** Latest arrival first; among equal arrivals, the higher ref first. */
function byLatestArrival(first: Delivery, second: Delivery): number {
  return arrivedAt(second) - arrivedAt(first) || compareDeliveryRefs(second.ref, first.ref);
}

/** The Delivered column's order: the latest arrival on top. Due dates are
 *  about the wait, and the wait is over — the pile reads as a log. */
export function sortDeliveredCards<TCard extends IColumnCard>(cards: TCard[]): TCard[] {
  return [...cards].sort(({ face: first }, { face: second }) => byLatestArrival(first, second));
}

/** Critical cards lead, every card otherwise keeping its place — the Delivered
 *  column on top of its arrival order. Delivery History stays a plain log. */
export function criticalFirst(cards: IColumnCard[]): IColumnCard[] {
  return [...cards].sort(({ face: first }, { face: second }) => Number(second.isCritical) - Number(first.isCritical));
}

/** The starred orders still on the board: a synced order is history. */
export function criticalOrderRefs(deliveries: Delivery[]): string[] {
  return deliveries.filter((delivery) => delivery.isCritical && !delivery.isSynced).map((delivery) => delivery.ref);
}

/** Delivered-column cap: the last 14 days stay visible, the rest sit behind
 *  a "Show all" link. Undated arrivals count as recent — hiding them would
 *  make an arrival invisible the moment it lands. */
export function partitionDelivered(
  cards: IColumnCard[],
  now: Date,
  tz: string,
): { recent: IColumnCard[]; older: IColumnCard[] } {
  const today = zonedDay(now.toISOString(), tz);
  const recent: IColumnCard[] = [];
  const older: IColumnCard[] = [];
  for (const card of cards) {
    const day = card.face.deliveredAt ? zonedDay(card.face.deliveredAt, tz) : null;
    (day && dayDiff(today, day) > DELIVERED_WINDOW_DAYS ? older : recent).push(card);
  }
  return { recent, older };
}

/** Delivery History's per-material view (it lived in the Delivered column's
 *  header until that column became unsynced-only). The view is synced HISTORY: only a fully synced order is counted
 *  into it — its stock is reconciled, and synced, it never drags. An order
 *  still waiting on its sync is not shown here at all; it lives in the
 *  per-delivery view until the sync lands (ruling 2026-09-28).
 *  One card per MATERIAL: the group's FACE is its newest arrival
 *  (an undated arrival counts as newest — it just landed), the rest fold
 *  beneath it as history, newest first. A truck carrying three materials
 *  therefore appears in three stacks, each card showing only that material's
 *  line — the column answers "how much of X arrived", and a truck mixes X and
 *  Y. Display-only: orders stay separate records, and the caller windows/sorts
 *  the FACES like any other cards. */
export function stackDelivered(cards: Delivery[]): IMaterialStack[] {
  const groups = new Map<string, Delivery[]>();
  for (const delivery of cards.filter((card) => card.isSynced)) {
    for (const line of delivery.lines) {
      const key = fold(line.itemName);
      const bucket = groups.get(key);
      if (bucket) bucket.push(delivery);
      else groups.set(key, [delivery]);
    }
  }
  return [...groups.entries()].map(([key, group]) => {
    const byArrival = [...group].sort(byLatestArrival);
    return { key, face: byArrival[0]!, history: byArrival.slice(1) };
  });
}

export function displayStatusName(
  status: DeliveryBoardStatus,
  t: (key: string) => string,
): string {
  if (DEFAULT_NAMES[status.id]?.includes(status.name)) return t(`logistics.board.defaults.${status.id}`);
  return status.name;
}

// Lives beside the error class it reads; re-exported so the barrel keeps naming it.
export { moveCardErrorTitleKey } from '../apis/logistics.js';
